import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Phone, Globe, ShieldCheck, AlertCircle, Search, Sparkles, Plus, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const STATE_FALLBACK = [
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "WI", name: "Wisconsin" },
];

export default function Resources() {
  const { lang, t } = useLang();
  const [state, setState] = useState(() => localStorage.getItem("pod_state") || "IL");
  const [county, setCounty] = useState(() => localStorage.getItem("pod_county") || "");
  const [zip, setZip] = useState(() => localStorage.getItem("pod_zip") || "");
  const [zipDetecting, setZipDetecting] = useState(false);
  const [zipMsg, setZipMsg] = useState("");
  const [zipMatches, setZipMatches] = useState([]);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState(STATE_FALLBACK);
  const [counties, setCounties] = useState([]);

  // Load supported states once
  useEffect(() => {
    api.get("/geo/states")
      .then((r) => setStates(r.data.states || STATE_FALLBACK))
      .catch(() => {});
  }, []);

  // Load county list whenever state changes
  useEffect(() => {
    if (!state) return;
    api.get("/geo/counties", { params: { state } })
      .then((r) => setCounties(r.data.counties || []))
      .catch(() => setCounties([]));
  }, [state]);

  // Fetch resources whenever state or county changes
  useEffect(() => {
    setLoading(true);
    localStorage.setItem("pod_state", state);
    localStorage.setItem("pod_county", county);
    api.get("/resources", { params: { state, county: county || undefined } })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [state, county]);

  // Auto-detect state+county from ZIP.
  const detectFromZip = async (rawZip) => {
    const z = (rawZip || "").trim();
    if (!/^\d{5}$/.test(z)) {
      setZipMsg(lang === "es" ? "Ingresa un ZIP de 5 dígitos." : "Enter a 5-digit ZIP.");
      setZipMatches([]);
      return;
    }
    setZipDetecting(true);
    setZipMsg("");
    setZipMatches([]);
    try {
      const { data } = await api.get("/geo/zip", { params: { zip: z } });
      if (data?.state && data?.county) {
        localStorage.setItem("pod_zip", z);
        setZip(z);
        applyMatch({ state: data.state, county: data.county });
        const matches = Array.isArray(data.matches) ? data.matches : [];
        setZipMatches(matches.length > 1 ? matches : []);
        setZipMsg(
          lang === "es"
            ? `Detectado: condado de ${data.county}, ${data.state}`
            : `Detected: ${data.county} County, ${data.state}`
        );
      }
    } catch {
      setZipMsg(
        lang === "es"
          ? "ZIP no encontrado. Selecciona tu condado manualmente."
          : "ZIP not in our map yet. Pick your county manually below."
      );
    } finally {
      setZipDetecting(false);
    }
  };

  const applyMatch = (m) => {
    setState(m.state);
    setCounty(m.county);
  };

  // Auto-run once on first mount if we already have a saved ZIP but no county
  useEffect(() => {
    if (zip && !county) detectFromZip(zip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { statewide, local } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (r) =>
      !q ||
      r.name.toLowerCase().includes(q) ||
      (r.purpose || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q);
    return {
      statewide: items.filter((r) => r.county === "Statewide").filter(match),
      local: items.filter((r) => r.county !== "Statewide").filter(match),
    };
  }, [items, query]);

  const anyMatches = statewide.length + local.length;
  const stateName = states.find((s) => s.code === state)?.name || state;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <MapPin className="w-6 h-6 text-[#C85A32]" /> {t("resources.title")}
        </h1>
        <p className="text-[#4A525A] mt-1">{t("resources.subtitle")}</p>
      </div>

      {/* ZIP auto-detect */}
      <div className="rounded-2xl border-2 border-dashed border-[#C85A32]/60 bg-[#FBF4EB] p-4" data-testid="zip-detect">
        <label htmlFor="resources-zip" className="text-xs uppercase tracking-widest font-extrabold text-[#C85A32] flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> {lang === "es" ? "Detección rápida por ZIP" : "Quick ZIP detect"}
        </label>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <input
            id="resources-zip"
            data-testid="resources-zip-input"
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
            onKeyDown={(e) => e.key === "Enter" && detectFromZip(zip)}
            placeholder={lang === "es" ? "Ej. 62701" : "e.g. 62701"}
            className="pod-focus flex-1 rounded-xl border-2 border-[#E4DDD3] bg-white px-3 py-3 text-base font-semibold tabular-nums"
            aria-label="ZIP code"
          />
          <button
            type="button"
            onClick={() => detectFromZip(zip)}
            disabled={zipDetecting}
            data-testid="resources-zip-btn"
            className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-60 text-white font-bold py-3 px-4 rounded-xl inline-flex items-center justify-center gap-2"
          >
            {zipDetecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            {lang === "es" ? "Detectar" : "Detect"}
          </button>
        </div>
        {zipMsg && (
          <div className="mt-2 text-xs text-[#4A525A]" data-testid="resources-zip-msg">{zipMsg}</div>
        )}
        {zipMatches.length > 1 && (
          <div className="mt-3" data-testid="resources-zip-border">
            <div className="text-xs text-[#4A525A] mb-1.5">
              {lang === "es"
                ? "Este ZIP cruza una frontera estatal. ¿En qué lado buscas ayuda?"
                : "This ZIP straddles a state line. Which side do you need help on?"}
            </div>
            <div className="flex flex-wrap gap-2">
              {zipMatches.map((m) => {
                const active = state === m.state && county === m.county;
                return (
                  <button
                    key={`${m.state}-${m.county}`}
                    type="button"
                    onClick={() => applyMatch(m)}
                    data-testid={`resources-zip-match-${m.state}-${m.county.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`pod-focus text-xs font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full border-2 transition-colors ${
                      active
                        ? "bg-[#C85A32] border-[#C85A32] text-white"
                        : "bg-white border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024]"
                    }`}
                  >
                    {m.state} · {m.county}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* State + County + Search */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label htmlFor="resources-state" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
            {lang === "es" ? "Estado" : "State"}
          </label>
          <select
            id="resources-state"
            data-testid="resources-state"
            value={state}
            onChange={(e) => { setState(e.target.value); setCounty(""); }}
            className="pod-focus mt-1 w-full rounded-xl border-2 border-[#E4DDD3] bg-white p-3 text-sm font-semibold"
          >
            {states.map((s) => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="resources-county" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
            {lang === "es" ? "Condado" : "County"}
          </label>
          <select
            id="resources-county"
            data-testid="resources-county"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            className="pod-focus mt-1 w-full rounded-xl border-2 border-[#E4DDD3] bg-white p-3 text-sm font-semibold"
          >
            <option value="">{lang === "es" ? `Todo ${stateName}` : `All ${stateName}`}</option>
            {counties.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="resources-search" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
            {lang === "es" ? "Buscar" : "Search"}
          </label>
          <div className="mt-1 relative">
            <Search className="w-4 h-4 text-[#707880] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              id="resources-search"
              data-testid="resources-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === "es" ? "vivienda, familia..." : "housing, family..."}
              className="pod-focus w-full rounded-xl border-2 border-[#E4DDD3] bg-white pl-9 pr-3 py-3 text-sm"
            />
          </div>
        </div>
      </div>

      {loading && <div className="text-[#4A525A]" data-testid="resources-loading">{t("auth.loading")}</div>}

      {/* Statewide section */}
      {!loading && statewide.length > 0 && (
        <section data-testid="resources-statewide">
          <h2 className="text-xs uppercase tracking-widest font-extrabold text-[#C85A32]">
            {lang === "es" ? `Ayuda estatal (todo ${stateName})` : `Statewide help (all ${stateName})`}
          </h2>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {statewide.map((r) => (
              <ResourceCard key={r.name} r={r} t={t} lang={lang} />
            ))}
          </div>
        </section>
      )}

      {/* Local county section */}
      {!loading && (
        <section data-testid="resources-local">
          <h2 className="text-xs uppercase tracking-widest font-extrabold text-[#C85A32]">
            {county
              ? (lang === "es" ? `En el condado de ${county}` : `In ${county} County`)
              : (lang === "es" ? "Por condado" : "By county")}
          </h2>
          {local.length === 0 ? (
            <div className="mt-3 space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-[#E4DDD3] bg-[#FBF9F5] p-5 text-sm text-[#4A525A]" data-testid="resources-local-empty">
                {county ? (
                  lang === "es"
                    ? `Aún no tenemos oficinas locales listadas en el condado de ${county}. Empieza con la ayuda estatal arriba — cubre todo ${stateName}.`
                    : `We don't have local offices listed in ${county} County yet. Start with the statewide help above — it covers all of ${stateName}.`
                ) : (
                  lang === "es" ? "Elige un condado para ver ayuda local." : "Pick a county to see local help."
                )}
              </div>
              {county && (
                <MissingCountyPrompt state={state} county={county} stateName={stateName} lang={lang} />
              )}
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {local.map((r) => (
                <ResourceCard key={r.name} r={r} t={t} lang={lang} />
              ))}
            </div>
          )}
        </section>
      )}

      {!loading && anyMatches === 0 && query && (
        <div className="text-[#4A525A]" data-testid="resources-empty">
          {lang === "es" ? "Nada coincide con tu búsqueda." : "Nothing matched your search."}
        </div>
      )}
    </div>
  );
}

function ResourceCard({ r, t, lang }) {
  return (
    <div
      data-testid={`resource-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-lg font-bold text-[#1C2024]">{r.name}</div>
        {r.verified ? (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-300 text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
            <ShieldCheck className="w-3 h-3" /> {t("resources.verified")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-300 text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
            <AlertCircle className="w-3 h-3" /> {lang === "es" ? "Verificar" : "Verify"}
          </span>
        )}
      </div>
      <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold mt-1">
        {r.category} · {r.county === "Statewide"
          ? (lang === "es" ? "Todo el estado" : `All ${r.state}`)
          : `${r.county} ${lang === "es" ? "" : "County"}`}
      </div>
      <p className="text-[#4A525A] mt-2">{r.purpose}</p>
      <div className="mt-3 space-y-1 text-sm">
        {r.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#C85A32]" />
            <a className="pod-focus underline" href={`tel:${r.phone}`}>{r.phone}</a>
          </div>
        )}
        {r.website && (
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#C85A32]" />
            <a className="pod-focus underline break-all" href={r.website} target="_blank" rel="noreferrer">{r.website}</a>
          </div>
        )}
      </div>
    </div>
  );
}

function MissingCountyPrompt({ state, county, stateName, lang }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // Reset the "done" flag when the user changes county
  useEffect(() => { setDone(false); setErr(""); }, [county, state]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr(lang === "es" ? "El nombre es obligatorio." : "Name is required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/resources/suggest", { state, county, name: name.trim(), website: website.trim(), phone: phone.trim(), note: note.trim() });
      setDone(true);
      setName(""); setWebsite(""); setPhone(""); setNote("");
    } catch (e2) {
      setErr(e2.response?.data?.detail || (lang === "es" ? "Intenta de nuevo." : "Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 text-emerald-900 p-5 flex items-start gap-3" data-testid="suggest-thanks">
        <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-extrabold">
            {lang === "es" ? "¡Gracias!" : "Thank you!"}
          </div>
          <div className="text-sm mt-0.5">
            {lang === "es"
              ? "Revisaremos tu sugerencia y la publicaremos si podemos verificarla."
              : "We'll review your suggestion and publish it once we can verify it."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-[#E4DDD3] bg-white p-5" data-testid="suggest-form">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="suggest-toggle"
        className="pod-focus w-full flex items-center justify-between gap-3 text-left"
      >
        <div>
          <div className="font-extrabold text-[#1C2024]">
            {lang === "es"
              ? `¿Sabes quién ayuda en el condado de ${county}?`
              : `Know who helps in ${county} County?`}
          </div>
          <div className="text-sm text-[#4A525A] mt-0.5">
            {lang === "es"
              ? "Cuéntanos y añadiremos el recurso tras verificarlo."
              : "Tell us and we'll add the resource once we verify it."}
          </div>
        </div>
        <span className="shrink-0 w-9 h-9 rounded-full bg-[#FAF5EC] text-[#C85A32] flex items-center justify-center">
          <Plus className={`w-4 h-4 transition-transform ${open ? "rotate-45" : ""}`} />
        </span>
      </button>

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label htmlFor="suggest-name" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
              {lang === "es" ? "Nombre de la organización *" : "Organization name *"}
            </label>
            <input
              id="suggest-name"
              data-testid="suggest-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="pod-focus mt-1 w-full rounded-xl border-2 border-[#E4DDD3] bg-white p-3 text-sm"
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="suggest-website" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
                {lang === "es" ? "Sitio web" : "Website"}
              </label>
              <input
                id="suggest-website"
                data-testid="suggest-website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                inputMode="url"
                maxLength={400}
                placeholder="https://"
                className="pod-focus mt-1 w-full rounded-xl border-2 border-[#E4DDD3] bg-white p-3 text-sm"
              />
            </div>
            <div>
              <label htmlFor="suggest-phone" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
                {lang === "es" ? "Teléfono" : "Phone"}
              </label>
              <input
                id="suggest-phone"
                data-testid="suggest-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                maxLength={40}
                className="pod-focus mt-1 w-full rounded-xl border-2 border-[#E4DDD3] bg-white p-3 text-sm"
              />
            </div>
          </div>
          <div>
            <label htmlFor="suggest-note" className="text-xs uppercase tracking-widest font-extrabold text-[#707880]">
              {lang === "es" ? "¿Qué hacen? (opcional)" : "What do they do? (optional)"}
            </label>
            <textarea
              id="suggest-note"
              data-testid="suggest-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={800}
              rows={3}
              className="pod-focus mt-1 w-full rounded-xl border-2 border-[#E4DDD3] bg-white p-3 text-sm"
            />
          </div>
          {err && <div className="text-sm text-red-700" data-testid="suggest-error">{err}</div>}
          <div className="flex items-center justify-end gap-2">
            <button
              type="submit"
              disabled={busy}
              data-testid="suggest-submit"
              className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-60 text-white font-bold py-2.5 px-5 rounded-xl inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {lang === "es" ? "Enviar sugerencia" : "Send suggestion"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
